import { alertDialog, confirmDialog, guardDialog, successDialog } from '@adornis/dialog/dialog-functions.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { html, nothing, type TemplateResult } from 'lit';
import {
  countFieldEngineersPendingOnboarding,
  type OnboardingResendResult,
  resendOnboardingForFilteredFieldEngineers,
} from '../../api/send-field-engineer-onboarding-email.js';

/**
 * Confirm → process → report flow for bulk-resending onboarding emails. Reads the *current* list
 * filter at click time (the filter instance is stable and mutated in place), counts the "yellow
 * marker" field engineers it would target (a login exists and they still owe a password change),
 * confirms the count, then generates a fresh random password and re-sends the onboarding email to
 * each — showing a loader and a result summary. Already-onboarded engineers are skipped server-side.
 */
const runResend = async (filter: AdornisFilter): Promise<void> => {
  let pending: number;
  try {
    pending = (await countFieldEngineersPendingOnboarding(filter)()) as number;
  } catch {
    await alertDialog(html`<p class="text-sm">Could not determine how many field engineers need onboarding.</p>`);
    return;
  }

  if (!pending) {
    await alertDialog(
      html`<p class="text-sm">
        No onboarding emails to send for the current view — everyone has either already changed their password or
        already received an onboarding email today.
      </p>`,
    );
    return;
  }

  const confirmed = await confirmDialog(
    html`
      <div class="flex flex-col gap-2 text-sm">
        <p>
          Generate a <b>new random password</b> and send the onboarding email to
          <b>${pending}</b> field engineer${pending === 1 ? '' : 's'} in the current view who still need to change
          their password (the yellow marker)?
        </p>
        <p class="text-base-content/60">
          Skipped, even if shown in the list: field engineers who have already logged in and changed their password,
          and anyone who already received an onboarding email today (max one per day).
        </p>
      </div>
    `,
    { acceptText: `Send ${pending} email${pending === 1 ? '' : 's'}`, declineText: 'Cancel', highlightDecline: true },
  ).catch(() => false);
  if (!confirmed) return;

  await guardDialog(
    () =>
      resendOnboardingForFilteredFieldEngineers(filter)({
        _id: 1,
        targeted: 1,
        sent: 1,
        failed: 1,
        failures: { _id: 1, name: 1, email: 1, error: 1 },
      }),
    {
      loadingText: 'Generating passwords and sending onboarding emails…',
      showLoaderModal: true,
      errorMessagePrefix: 'Could not send onboarding emails',
      success: async (result: OnboardingResendResult) => {
        const { sent = 0, targeted = 0 } = result;
        const failures = result.failures ?? [];
        await successDialog(
          html`
            <div class="flex flex-col gap-3 text-sm">
              <p>Sent <b>${sent}</b> of <b>${targeted}</b> onboarding email${targeted === 1 ? '' : 's'}.</p>
              ${failures.length
                ? html`
                    <div class="flex flex-col gap-1">
                      <p class="font-medium text-error">
                        ${failures.length} failed — open each to fix the address, then re-run:
                      </p>
                      <div class="max-h-64 divide-y divide-base-200 overflow-auto rounded border border-base-300">
                        ${failures.map(
                          f => html`
                            <div class="flex flex-col px-3 py-2">
                              <a
                                href="/field-engineers/${f._id}"
                                target="_blank"
                                rel="noopener"
                                class="link link-primary font-medium"
                              >
                                ${f.name || f.email || f._id}
                              </a>
                              ${f.email ? html`<span class="text-xs text-base-content/60">${f.email}</span>` : nothing}
                              ${f.error ? html`<span class="text-xs text-error/80">${f.error}</span>` : nothing}
                            </div>
                          `,
                        )}
                      </div>
                    </div>
                  `
                : nothing}
            </div>
          `,
          'Close',
          { modalBoxClasses: 'max-w-lg' },
        );
      },
    },
  );
};

/** Toolbar button that triggers the bulk onboarding re-send for the currently filtered field engineers. */
export const FieldEngineerOnboardingButton = ({ filter }: { filter: AdornisFilter }): TemplateResult => html`
  <button class="btn btn-outline btn-sm gap-2" @click=${() => void runResend(filter)}>
    <span class="icon-[mdi--email-fast-outline] text-base"></span>
    Send onboarding email
  </button>
`;
